// src/services/monthlyReportService.mjs
import BaseApiService from "./baseApiService.mjs";

/**
 * An approval error that keeps what the server said about it. An approval
 * answered with 409 OPEN_QUESTIONS carries the unresolved clarification
 * questions, which the caller shows before retrying with the acknowledgement
 * (clarifications/util/confirmOpenQuestions.mjs).
 */
const approvalError = (error, fallback) => {
  const e = new Error(error.response?.data?.message || fallback);
  e.status = error.response?.status;
  e.code = error.response?.data?.code;
  e.openQuestions = error.response?.data?.openQuestions;
  return e;
};


class MonthlyReportService extends BaseApiService {
  constructor() {
    super();
    this.baseURL = '/monthly-reports';
  }

  /**
   * The versions archived for a report: which exist and what happened in each.
   * Index only - the snapshots themselves come from getReportVersion, because a
   * long-running report's archive is large and the viewer shows one at a time.
   */
  async getReportVersions(reportId) {
    try {
      const response = await this.api.get(`${this.baseURL}/${reportId}/versions`);
      return response.data?.data || null;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to load version history');
    }
  }

  /** One archived version, with its `fidelity` saying how complete it is. */
  async getReportVersion(reportId, version) {
    try {
      const response = await this.api.get(`${this.baseURL}/${reportId}/versions/${version}`);
      return response.data?.data || null;
    } catch (error) {
      throw new Error(error.response?.data?.message || `Failed to load version ${version}`);
    }
  }

  // Get claims eligible for a month's report (backlog: approved, unpaid, not yet
  // in a non-rejected report). Used to review/deselect claims before generating.
  async getEligibleClaims(month, year, region) {
    try {
      const response = await this.api.get(`${this.baseURL}/generate/eligible-claims`, {
        params: { month, year, region }
      });
      return response.data?.data?.claims || [];
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch eligible claims');
    }
  }

  // Generate monthly report. excludeClaimIds = claims the admin deferred to a
  // later month (left out of this report).
  async generateMonthlyReport(month, year, region, excludeClaimIds = []) {
    try {
      const response = await this.api.post(`${this.baseURL}/generate/report`, {
        month,
        year,
        region,
        excludeClaimIds
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to generate monthly report');
    }
  }

  // Pull selected approved claims into an existing DRAFT report (admin). Pass an
  // array of claimIds to add only those (empty = all). Returns { success, added }.
  async pullApprovedClaims(reportId, claimIds = []) {
    try {
      const response = await this.api.post(`${this.baseURL}/${reportId}/pull-approved-claims`, { claimIds });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to pull approved claims');
    }
  }

  // Send report to auditor.
  //
  // The server works out the approval route (2026-10-22). The two ids are
  // read only for a stage that has nobody to sign it - see getApprovalRoute -
  // and ignored otherwise: who approves a regional director's or a global
  // director's stage while they are away is now theirs to decide, by delegation.
  async sendToAuditor(reportId, path = {}) {
    try {
      const response = await this.api.post(
        `${this.baseURL}/send-to-auditor/${reportId}`,
        {
          regionalApproverId: path.regionalApproverId ?? null,
          globalApproverId: path.globalApproverId ?? null,
          // Submitting is signed (SignatureModal); the server refuses without it.
          signature: path.signature ?? undefined,
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to send report to auditor');
    }
  }

  // The route this report would take if submitted now: who approves each
  // stage, who covers by delegation, and - only where a stage has nobody -
  // who the admin may choose from. { region, regional: {...}, final: {...} }
  async getApprovalRoute(reportId) {
    try {
      const response = await this.api.get(`${this.baseURL}/${reportId}/approval-route`);
      return response.data?.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to load the approval route');
    }
  }

  // Who may be named to approve at the regional-director stage.
  async getApproverCandidates(region = '') {
    try {
      const response = await this.api.get(`${this.baseURL}/approver-candidates`, {
        params: region ? { region } : {},
      });
      return response.data?.data || { regionalDirectors: [], others: [] };
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to load approver candidates');
    }
  }

  // Get monthly report by ID
  async getMonthlyReport(reportId) {
    try {
      const response = await this.api.get(`${this.baseURL}/reports/report/${reportId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch monthly report');
    }
  }

  // Update monthly report (auditor actions)
  async updateMonthlyReport(reportId, updateData) {
    try {
      const response = await this.api.put(`${this.baseURL}/update/report/${reportId}`, updateData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update monthly report');
    }
  }

  // Get auditor dashboard data
  async getAuditorDashboard() {
    try {
      const response = await this.api.get(`${this.baseURL}/auditor/dashboard`);
      return response.data;
    } catch (error) {
      // The status is kept so the page can tell "you have no access" from
      // "something went wrong" - both used to surface as the same red line.
      const e = new Error(error.response?.data?.message || 'Failed to fetch auditor dashboard');
      e.status = error.response?.status;
      throw e;
    }
  }

  async deleteReport(reportId) {
    try {
      const response = await this.api.delete(`${this.baseURL}/delete/report/${reportId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete report');
    }
  }

  // Submit report for approval (admin submits to auditor)
  async submitReport(reportId, comments = '') {
    try {
      const response = await this.api.post(`${this.baseURL}/${reportId}/submit`, { comments });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to submit report');
    }
  }

  // Approve report (auditor, regional_director, global_director, finance_director).
  // acknowledgeOpenQuestions: the approver has seen the open-questions warning.
  // signature: from SignatureModal - required at the regional and final stages.
  // takeOverFinal: a final approver confirmed taking over from the one named.
  async approveReport(reportId, comments = '', { acknowledgeOpenQuestions = false, signature, takeOverFinal = false } = {}) {
    try {
      const response = await this.api.post(`${this.baseURL}/${reportId}/approve`, {
        comments, acknowledgeOpenQuestions, signature, takeOverFinal,
      });
      return response.data;
    } catch (error) {
      throw approvalError(error, 'Failed to approve report');
    }
  }

  // Reject report (requires reason)
  async rejectReport(reportId, reason, comments = '') {
    try {
      const response = await this.api.post(`${this.baseURL}/${reportId}/reject`, { reason, comments });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to reject report');
    }
  }

  // Get reports pending approval for current user's role
  async getPendingApproval() {
    try {
      const response = await this.api.get(`${this.baseURL}/pending-approval`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch pending approvals');
    }
  }

  // Get approval chain (audit trail) for a report
  async getApprovalChain(reportId) {
    try {
      const response = await this.api.get(`${this.baseURL}/${reportId}/approval-chain`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch approval chain');
    }
  }

  // Verify report integrity (check cryptographic hash chain)
  async verifyIntegrity(reportId) {
    try {
      const response = await this.api.get(`${this.baseURL}/${reportId}/verify-integrity`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to verify report integrity');
    }
  }

  // Get full verification certificate (includes approval chain + claim hashes)
  async getVerificationCertificate(reportId) {
    try {
      const response = await this.api.get(`${this.baseURL}/${reportId}/verification-certificate`);
      return response.data.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch verification certificate');
    }
  }

  // Get Regional Director dashboard
  async getRegionalDirectorDashboard() {
    try {
      const response = await this.api.get(`${this.baseURL}/director/regional/dashboard`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch regional director dashboard');
    }
  }

  // Get Global Director dashboard
  async getGlobalDirectorDashboard() {
    try {
      const response = await this.api.get(`${this.baseURL}/director/global/dashboard`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch global director dashboard');
    }
  }

  // Get supporting documents for all claims in a report
  async getReportSupportingDocuments(reportId) {
    try {
      const response = await this.api.get(`${this.baseURL}/${reportId}/supporting-documents`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch supporting documents');
    }
  }

  // Download all supporting documents as ZIP
  // Digitally signed (PAdES) report PDF, generated server-side. Signing cannot
  // happen in the browser, so downloads come from the backend.
  async downloadSignedReportPDF(reportId) {
    try {
      const response = await this.api.get(`${this.baseURL}/${reportId}/pdf`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to download signed report PDF');
    }
  }

  async downloadSupportingDocumentsZip(reportId) {
    try {
      const response = await this.api.get(`${this.baseURL}/${reportId}/supporting-documents/download`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      // With responseType 'blob' the ERROR body is a Blob too, so .message is
      // undefined and a refusal read as a generic failure. Decode it to get the
      // server's reason (e.g. "You do not have access to this report").
      let message = 'Failed to download supporting documents';
      const body = error.response?.data;
      if (body && typeof body.text === 'function') {
        try { message = JSON.parse(await body.text()).message || message; } catch { /* not JSON */ }
      }
      const e = new Error(message);
      e.status = error.response?.status;
      throw e;
    }
  }

  // The final-approval archive: the signed report, its spreadsheet, every
  // claim's PDF and workbook and every receipt, grouped by person. Rebuilt by
  // the server on each request, and its MANIFEST.txt says whether it still
  // matches the approval. Only exists once a report has final approval.
  async downloadApprovalArchive(reportId) {
    try {
      const response = await this.api.get(`${this.baseURL}/${reportId}/approval-archive`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      // A blob error body has to be read before its message can be shown.
      let message = 'Failed to download the approval archive';
      const body = error.response?.data;
      if (body && typeof body.text === 'function') {
        try { message = JSON.parse(await body.text()).message || message; } catch { /* not JSON */ }
      }
      const e = new Error(message);
      e.status = error.response?.status;
      throw e;
    }
  }

  // Get all admin-approved claims not yet individually audited
  async getPendingAuditClaims() {
    try {
      const response = await this.api.get('/auditor/claims/pending-audit');
      return response.data;
    } catch (error) {
      const e = new Error(error.response?.data?.message || 'Failed to fetch pending audit claims');
      e.status = error.response?.status;
      throw e;
    }
  }

  /**
   * Contractor claims awaiting the auditor's FINAL approval.
   *
   * Separate from getPendingAuditClaims because the auditor is doing a
   * different job: on a staff claim the audit is a pre-approval and the
   * directors still sign, on a contractor's nobody signs after them.
   */
  async getContractorClaims() {
    try {
      const response = await this.api.get('/auditor/claims/contractor-approvals');
      return response.data;
    } catch (error) {
      const e = new Error(error.response?.data?.message || 'Failed to fetch contractor claims');
      e.status = error.response?.status;
      throw e;
    }
  }

  // Contractor claims the auditor has already decided: { approved, sentBack }.
  async getContractorHistory() {
    try {
      const response = await this.api.get('/auditor/claims/contractor-history');
      return response.data?.data || { approved: [], sentBack: [] };
    } catch (error) {
      const e = new Error(error.response?.data?.message || 'Failed to fetch contractor claim history');
      e.status = error.response?.status;
      throw e;
    }
  }

  // Individually audit (approve) a single claim
  async auditClaim(claimId, claimType, comments = '', { acknowledgeOpenQuestions = false } = {}) {
    try {
      const type = claimType === 'ExpenseClaim' ? 'expense' : 'perdiem';
      const response = await this.api.post(`/auditor/claims/${type}/${claimId}/audit`, {
        comments, acknowledgeOpenQuestions,
      });
      return response.data;
    } catch (error) {
      throw approvalError(error, 'Failed to audit claim');
    }
  }

  // Return a pending claim for revision (→ under_review). Comment required.
  async returnClaim(claimId, claimType, comments) {
    try {
      const type = claimType === 'ExpenseClaim' ? 'expense' : 'perdiem';
      const response = await this.api.post(`/auditor/claims/${type}/${claimId}/return`, { comments });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to return claim');
    }
  }

  // ---- Guided claim-by-claim review (split-screen walkthrough) ----

  // One claim's review bundle: the claim for the left pane, plus its supporting
  // documents already ordered and signed for the right pane.
  async getClaimReviewBundle(reportId, claimId, claimType) {
    try {
      const type = claimType === 'ExpenseClaim' ? 'expense' : 'perdiem';
      const response = await this.api.get(
        `${this.baseURL}/${reportId}/review/claims/${type}/${claimId}`
      );
      return response.data?.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to load the claim for review');
    }
  }

  // Same bundle for a claim in the Claims Pre-Approval list, which has no
  // report context yet. Shares the backend's builder, so the shape is identical.
  async getPendingClaimReviewBundle(claimId, claimType) {
    try {
      const type = claimType === 'ExpenseClaim' ? 'expense' : 'perdiem';
      const response = await this.api.get(`/auditor/claims/${type}/${claimId}/review`);
      return response.data?.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to load the claim for review');
    }
  }

  // Record that the caller examined this claim. Auditor → individual audit;
  // director → per-claim review progress.
  async markClaimReviewed(reportId, claimId, claimType, comments = '', { acknowledgeOpenQuestions = false } = {}) {
    try {
      const type = claimType === 'ExpenseClaim' ? 'expense' : 'perdiem';
      const response = await this.api.post(
        `${this.baseURL}/${reportId}/review/claims/${type}/${claimId}/mark`,
        { comments, acknowledgeOpenQuestions }
      );
      return response.data;
    } catch (error) {
      throw approvalError(error, 'Failed to mark the claim as reviewed');
    }
  }

  // Flag a claim for follow-up. A blank/null reason clears the caller's own flag.
  async flagClaim(reportId, claimId, claimType, reason) {
    try {
      const type = claimType === 'ExpenseClaim' ? 'expense' : 'perdiem';
      const response = await this.api.post(
        `${this.baseURL}/${reportId}/review/claims/${type}/${claimId}/flag`,
        { reason }
      );
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to flag the claim');
    }
  }

  // Approved vs returned audit decisions, with return rates. Read from the
  // approval hash chain so a claim returned then approved still counts as a
  // return - current status would hide it.
  async getAuditOutcomes({ from = '', to = '', region = '' } = {}) {
    try {
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      if (region && region !== 'all') params.region = region;
      const response = await this.api.get('/auditor/claims/audit-outcomes', { params });
      return response.data?.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to build the rejection report');
    }
  }

  // ---- Delegation: handing report review over while away ----

  async getDelegations() {
    try {
      const response = await this.api.get('/auditor/delegations');
      return response.data?.data || [];
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to load delegations');
    }
  }

  // Who can be delegated to, split into finance director(s) and other staff,
  // plus the valid region list.
  async getDelegationCandidates() {
    try {
      const response = await this.api.get('/auditor/delegations/candidates');
      return response.data?.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to load candidates');
    }
  }

  // payload: { delegateKind:'user'|'finance_director', delegateUserId?, regions[], returnDate?, note? }
  async createDelegation(payload) {
    try {
      const response = await this.api.post('/auditor/delegations', payload);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create the delegation');
    }
  }

  async revokeDelegation(id) {
    try {
      const response = await this.api.post(`/auditor/delegations/${id}/revoke`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to revoke the delegation');
    }
  }

  // Requests waiting on the CALLER to accept or decline. Returns [] rather than
  // throwing on failure: this drives a prompt shown alongside unrelated content,
  // and a delegation lookup problem must not break the page around it.
  async getDelegationInvitations() {
    try {
      const response = await this.api.get('/auditor/delegations/invitations');
      return response.data?.data || [];
    } catch (error) {
      return [];
    }
  }

  // decision: 'accept' | 'decline'. A decline requires a reason.
  async respondToDelegation(id, decision, reason = '') {
    try {
      const response = await this.api.post(
        `/auditor/delegations/${id}/respond`,
        { decision, reason }
      );
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to record your response');
    }
  }

  // ---- Director delegation: a regional or global director's approval ----
  // Same lifecycle as the auditor's above; a separate store on the server.

  async getDirectorDelegations() {
    try {
      const response = await this.api.get(`${this.baseURL}/director-delegations`);
      return response.data?.data || [];
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to load delegations');
    }
  }

  // { stage: 'regional'|'final', regions: [], users: [] }
  async getDirectorDelegationCandidates() {
    try {
      const response = await this.api.get(`${this.baseURL}/director-delegations/candidates`);
      return response.data?.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to load candidates');
    }
  }

  // payload: { delegateUserId, regions? (global director), returnDate, note? }
  async createDirectorDelegation(payload) {
    try {
      const response = await this.api.post(`${this.baseURL}/director-delegations`, payload);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create the delegation');
    }
  }

  async revokeDirectorDelegation(id) {
    try {
      const response = await this.api.post(`${this.baseURL}/director-delegations/${id}/revoke`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to revoke the delegation');
    }
  }

  // [] on failure, as getDelegationInvitations: it drives a home-page prompt.
  async getDirectorDelegationInvitations() {
    try {
      const response = await this.api.get(`${this.baseURL}/director-delegations/invitations`);
      return response.data?.data || [];
    } catch (error) {
      return [];
    }
  }

  async respondToDirectorDelegation(id, decision, reason = '') {
    try {
      const response = await this.api.post(
        `${this.baseURL}/director-delegations/${id}/respond`,
        { decision, reason }
      );
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to record your response');
    }
  }

  // List approved claims available to pull into a draft report.
  async getAvailableClaims(reportId) {
    try {
      const response = await this.api.get(`${this.baseURL}/${reportId}/available-claims`);
      return response.data?.data?.claims || [];
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch available claims');
    }
  }
}


const monthlyReportService = new MonthlyReportService();
export default monthlyReportService;


